import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import MenuItem from '@material-ui/core/MenuItem'
import MuiSelect from '@material-ui/core/Select'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import District from '../../models/District'
import School from '../../models/School'
import KonvaCanvas from '../NewTemplateEditor/KonvaCanvas/KonvaCanvas'
import SpellCheckPanel from './SpellCheckPanel'
import styles from './CustomerTemplateEditor.module.scss'

/**
 * CustomerTemplateEditor - A component for customers to preview and fill in template-driven products
 *
 * Props:
 * - template: The template object with dimensions and settings
 * - fields: Array of field objects from the template
 * - imageUrl: URL of the template background image
 * - itemId: The item ID for adding to cart
 * - cartId: The cart ID
 * - submitUrl: The URL to POST form data to
 * - itemName: Name of the item
 * - itemNumber: Item number (e.g., "1178c")
 * - itemDescription: Description of the item (optional)
 * - quantityOptions: Array of quantity options [{name, label, default}]
 * - previewImageUrl: URL of the item preview image (optional)
 * - hasSchoolInfo: Whether school info has already been collected
 * - schoolId: Current school ID (if set)
 * - schoolName: Current school name (if set)
 * - schoolEmployee: Current school employee position (if set)
 * - saveSchoolInfoUrl: URL to POST school info to
 */
export default function CustomerTemplateEditor({
  template,
  fields: fieldsArray,
  imageUrl,
  itemId,
  cartId,
  submitUrl,
  itemName,
  itemNumber,
  itemDescription,
  quantityOptions = [{ name: 'quantity', label: 'Quantity', default: 1 }],
  previewImageUrl,
  // School info props
  hasSchoolInfo = false,
  schoolId = null,
  schoolName = null,
  schoolEmployee = null,
  saveSchoolInfoUrl = null,
  // Proof generation
  generateProofUrl = null,
  // Fonts for the toolbar
  fonts = [],
  // Draft persistence
  draftSaveUrl = null,
  initialDraft = {},
  // Full field state for restoration (includes positions, custom fields, etc.)
  initialFieldState = null,
  // Multipage support
  pages: initialPages = [],
}) {
  // Step management: 'school_info', 'template_edit', or 'proof_review'
  const [currentStep, setCurrentStep] = useState(hasSchoolInfo ? 'template_edit' : 'school_info')

  // Proof generation state
  const [isGeneratingProof, setIsGeneratingProof] = useState(false)
  const [proofPdfUrl, setProofPdfUrl] = useState(null)
  const [proofPageImages, setProofPageImages] = useState([])
  const [proofCurrentPage, setProofCurrentPage] = useState(0)
  const [proofError, setProofError] = useState(null)

  // School selection state
  const [selectedDistrict, setSelectedDistrict] = useState(null)
  const [selectedSchool, setSelectedSchool] = useState(
    schoolId && schoolName ? { value: schoolId, label: schoolName } : null
  )
  const [selectedPosition, setSelectedPosition] = useState(
    schoolEmployee ? { value: schoolEmployee, label: schoolEmployee } : null
  )
  const [isSavingSchool, setIsSavingSchool] = useState(false)
  const [schoolError, setSchoolError] = useState(null)

  // Current school name for template fields
  const [currentSchoolName, setCurrentSchoolName] = useState(schoolName || '')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState({})
  const [approved, setApproved] = useState(false)
  const [checkoutUrl, setCheckoutUrl] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [hideFrameEdges, setHideFrameEdges] = useState(false)
  const [showSafetyZone, setShowSafetyZone] = useState(true)
  const [showSpellCheck, setShowSpellCheck] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const initialCanvasFieldsRef = useRef(null)

  // Multipage support: template background = Page 1, TemplatePages = Page 2+
  const [currentPage, setCurrentPage] = useState(0)
  const isMultipage = initialPages.length > 0
  const allPages = useMemo(() => {
    if (!isMultipage) return []
    return [
      { id: null, position: 1, imageUrl: imageUrl },
      ...initialPages.map((p, idx) => ({ ...p, position: idx + 2 }))
    ]
  }, [initialPages, imageUrl, isMultipage])
  const currentPageData = isMultipage ? allPages[currentPage] : null
  const currentPageId = currentPageData ? currentPageData.id : null
  const currentImageUrl = isMultipage ? (currentPageData?.imageUrl || imageUrl) : imageUrl

  // Helper to check if a field is a School Name field
  const isSchoolNameField = (field, columnName, original = null) => {
    const isSchoolNameColumn = columnName &&
      columnName.toLowerCase().includes('school') &&
      columnName.toLowerCase().includes('name')
    const hasSchoolNamePlaceholder =
      field.text === 'School Name' ||
      field.sampleText === 'School Name' ||
      field.sample_text === 'School Name' ||
      original?.text === 'School Name' ||
      original?.sample_text === 'School Name' ||
      original?.sampleText === 'School Name'
    return isSchoolNameColumn || hasSchoolNamePlaceholder
  }

  // Convert fields array to object format for KonvaCanvas (keyed by ID)
  // This allows local editing without saving to the database
  // If initialFieldState is provided, use it to restore complete field state (including custom fields)
  // Otherwise, build from fieldsArray and restore text values from initialDraft
  const [canvasFields, setCanvasFields] = useState(() => {
    // If we have saved field state, restore it completely (includes custom fields)
    if (initialFieldState && Array.isArray(initialFieldState) && initialFieldState.length > 0) {
      const fieldsObj = {}
      initialFieldState.forEach(field => {
        // Check if this is a School Name field that should show actual school name
        const shouldUseSchoolName = schoolName && isSchoolNameField(field, field.label)

        fieldsObj[field.id] = {
          id: field.id,
          left: field.left,
          top: field.top,
          width: field.width,
          height: field.height,
          textSize: field.textSize,
          color: field.color,
          align: field.align,
          spacing: field.spacing,
          text: shouldUseSchoolName ? schoolName : field.text,
          sampleText: shouldUseSchoolName ? null : field.sampleText,
          wrap: field.wrap,
          fontName: field.fontName,
          fontId: field.fontId,
          fontStyle: field.fontStyle || 'normal',
          textDecoration: field.textDecoration || '',
          autoFit: field.autoFit,
          minFontSize: field.minFontSize,
          label: field.label,
          columnType: field.columnType,
          isCustomField: field.isCustomField || false,
          templatePageId: field.templatePageId || null,
        }
      })
      return fieldsObj
    }

    // Otherwise, build from template fields array
    const fieldsObj = {}
    fieldsArray.forEach(field => {
      const columnName = field.name || field.column_name
      const draftValue = columnName && initialDraft ? initialDraft[columnName] : null

      // Check if this is a School Name field that should show actual school name
      const shouldUseSchoolName = schoolName && isSchoolNameField(field, columnName)

      fieldsObj[field.id] = {
        id: field.id,
        left: field.x,
        top: field.y,
        width: field.width,
        height: field.height,
        textSize: field.text_size || field.textSize,
        color: field.color,
        align: field.align,
        spacing: field.spacing,
        // Use school name for School Name fields, otherwise draft value or original text
        text: shouldUseSchoolName ? schoolName : (draftValue || field.text),
        sampleText: shouldUseSchoolName ? null : (draftValue ? null : (field.sample_text || field.sampleText)),
        wrap: field.wrap,
        fontName: field.fontName || field.font?.name,
        fontId: field.fontId || field.font?.id,
        fontStyle: field.fontStyle || field.font_style || 'normal',
        textDecoration: field.textDecoration || field.text_decoration || '',
        autoFit: field.auto_fit || field.autoFit,
        minFontSize: field.minFontSize || field.min_font_size,
        label: field.field_label || field.fieldLabel || field.name || field.column_name,
        columnType: field.column_type || field.columnType,
        customerEditable: field.customer_editable || field.customerEditable,
        templatePageId: field.template_page_id || field.templatePageId || null,
        // Don't include 'object' - customers don't save to database
      }
    })
    return fieldsObj
  })

  // Track dirty state: capture initial snapshot and compare on changes
  useEffect(() => {
    if (!initialCanvasFieldsRef.current && Object.keys(canvasFields).length > 0) {
      initialCanvasFieldsRef.current = JSON.stringify(canvasFields)
    }
  }, [canvasFields])

  useEffect(() => {
    if (!initialCanvasFieldsRef.current) return
    const current = JSON.stringify(canvasFields)
    setIsDirty(current !== initialCanvasFieldsRef.current)
  }, [canvasFields])

  // Warn on browser navigation (refresh, back) with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Intercept the modal X close button when dirty
  useEffect(() => {
    const handler = (e) => {
      const closeBtn = e.target.closest('.custom-modal-close-button')
      if (closeBtn && isDirty) {
        if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          e.stopPropagation()
          e.preventDefault()
        }
      }
    }
    document.addEventListener('click', handler, true) // capture phase
    return () => document.removeEventListener('click', handler, true)
  }, [isDirty])

  // Filter fields for current page (multipage) or show all (single-page)
  const visibleCanvasFields = useMemo(() => {
    if (!isMultipage) return canvasFields
    const filtered = {}
    Object.entries(canvasFields).forEach(([id, field]) => {
      const belongsToCurrentPage = field.templatePageId === currentPageId ||
        (currentPage === 0 && !field.templatePageId)
      if (belongsToCurrentPage) {
        filtered[id] = field
      }
    })
    return filtered
  }, [canvasFields, currentPageId, currentPage, isMultipage])

  // Refs for auto-save
  const saveTimeoutRef = useRef(null)
  const lastSavedRef = useRef(JSON.stringify(initialDraft || {}))

  const positionOptions = [
    { value: 'Principal', label: 'Principal' },
    { value: 'Vice-Principal', label: 'Vice-Principal' },
    { value: 'Office Manager', label: 'Office Manager' },
    { value: 'Secretary', label: 'Secretary' },
    { value: 'Teacher', label: 'Teacher' },
    { value: 'Educational Support', label: 'Educational Support' },
  ]

  // Build a map of field ID to original field data (for column names)
  const fieldIdToOriginal = useMemo(() => {
    const map = {}
    fieldsArray.forEach(field => {
      map[field.id] = field
    })
    return map
  }, [fieldsArray])

  // Derive template data from canvasFields for form submission
  // This converts from field ID-keyed object to column_name-keyed object
  const getTemplateDataFromCanvas = useCallback(() => {
    const templateData = {}
    Object.values(canvasFields).forEach(field => {
      const original = fieldIdToOriginal[field.id]
      const columnName = original?.name || original?.column_name || original?.columnName
      if (columnName) {
        // Use text if set, otherwise fall back to sampleText
        const textValue = field.text != null && field.text !== '' ? field.text : (field.sampleText || '')
        templateData[columnName] = textValue
      }
    })
    return templateData
  }, [canvasFields, fieldIdToOriginal])

  // Get full field state for persistence (includes positions, styles, custom fields)
  // This allows customers to return and see their exact layout
  const getFullFieldState = useCallback(() => {
    return Object.values(canvasFields).map(field => ({
      id: field.id,
      left: field.left,
      top: field.top,
      width: field.width,
      height: field.height,
      textSize: field.textSize,
      color: field.color,
      align: field.align,
      spacing: field.spacing,
      text: field.text,
      sampleText: field.sampleText,
      wrap: field.wrap,
      fontName: field.fontName,
      fontId: field.fontId,
      fontStyle: field.fontStyle,
      textDecoration: field.textDecoration,
      autoFit: field.autoFit,
      minFontSize: field.minFontSize,
      label: field.label,
      columnType: field.columnType,
      isCustomField: field.isCustomField || false,
      templatePageId: field.templatePageId || null,
    }))
  }, [canvasFields])

  // Auto-save drafts when canvas fields change
  // TODO: Re-enable after fixing draft data structure
  // Currently disabled because it was saving corrupted data
  /*
  useEffect(() => {
    if (!draftSaveUrl) return

    const templateData = getTemplateDataFromCanvas()
    const currentDataStr = JSON.stringify(templateData)

    // Skip if data hasn't changed
    if (currentDataStr === lastSavedRef.current) return

    // Debounce: clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Save after 1.5 seconds of no changes
    saveTimeoutRef.current = setTimeout(() => {
      fetch(draftSaveUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ template_data: templateData })
      })
        .then(response => {
          if (response.ok) {
            lastSavedRef.current = currentDataStr
            console.log('Draft saved')
          }
        })
        .catch(error => console.error('Error saving draft:', error))
    }, 1500)

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [canvasFields, draftSaveUrl, getTemplateDataFromCanvas])
  */

  // Update school name in canvasFields when it changes
  useEffect(() => {
    if (currentSchoolName) {
      setCanvasFields(prev => {
        const updated = { ...prev }
        Object.keys(updated).forEach(fieldId => {
          const field = updated[fieldId]
          const original = fieldIdToOriginal[fieldId]
          const columnName = original?.name || original?.column_name || original?.columnName

          if (isSchoolNameField(field, columnName, original)) {
            // Update the field with the actual school name
            updated[fieldId] = { ...updated[fieldId], text: currentSchoolName, sampleText: null }
          }
        })
        return updated
      })
    }
  }, [currentSchoolName, fieldIdToOriginal])

  // Initialize quantities
  const [quantities, setQuantities] = useState(() => {
    const initial = {}
    quantityOptions.forEach(opt => {
      initial[opt.name] = opt.default || 1
    })
    return initial
  })

  // District search
  const loadDistrictOptions = (inputValue, callback) => {
    District
      .where({ name: { match: inputValue } })
      .order({ name: 'asc' })
      .per(50)
      .all()
      .then((res) => {
        const options = res.data.map((e) => ({ label: e.name, value: e.id, data: e }))
        callback(options)
      })
      .catch(() => callback([]))
  }

  // School search (filtered by district)
  const loadSchoolOptions = (inputValue, callback) => {
    if (!selectedDistrict) {
      callback([])
      return
    }
    School
      .where({
        district_id: selectedDistrict.data.id,
        name: { match: inputValue },
      })
      .order({ name: 'asc' })
      .per(50)
      .all()
      .then((res) => {
        const options = res.data.map((e) => ({ label: e.name, value: e.id, data: e }))
        callback(options)
      })
      .catch(() => callback([]))
  }

  const handleDistrictChange = (option) => {
    setSelectedDistrict(option)
    setSelectedSchool(null) // Reset school when district changes
    setSchoolError(null)
  }

  const handleSchoolChange = (option) => {
    setSelectedSchool(option)
    setSchoolError(null)
  }

  const handlePositionChange = (option) => {
    setSelectedPosition(option)
    setSchoolError(null)
  }

  const handleSaveSchoolInfo = async () => {
    if (!selectedSchool || !selectedPosition) {
      setSchoolError('Please select your school and position')
      return
    }

    setIsSavingSchool(true)
    setSchoolError(null)

    try {
      const response = await fetch(saveSchoolInfoUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          school_id: selectedSchool.value,
          school_employee: selectedPosition.value,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setCurrentSchoolName(data.school_name || selectedSchool.label)
        setCurrentStep('template_edit')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setSchoolError(errorData.error || 'Failed to save school information. Please try again.')
      }
    } catch (error) {
      console.error('Error saving school info:', error)
      setSchoolError('An error occurred. Please try again.')
    } finally {
      setIsSavingSchool(false)
    }
  }

  // Generate proof PDF
  const handleGenerateProof = async () => {
    if (!generateProofUrl) {
      setProofError('Proof generation is not available')
      return
    }

    setIsGeneratingProof(true)
    setProofError(null)
    setProofPdfUrl(null)

    try {
      // Derive template data from the canvas fields
      const templateData = getTemplateDataFromCanvas()
      // Get full field state for accurate PDF rendering
      const fieldState = getFullFieldState()

      const response = await fetch(generateProofUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          template_data: templateData,
          field_state: fieldState,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        setProofPdfUrl(data.pdf_url)
        // Open PDF in new tab
        window.open(data.pdf_url, '_blank')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setProofError(errorData.error || 'Failed to generate proof. Please try again.')
      }
    } catch (error) {
      console.error('Error generating proof:', error)
      setProofError('An error occurred. Please try again.')
    } finally {
      setIsGeneratingProof(false)
    }
  }

  const handleQuantityChange = useCallback((name, value) => {
    const numValue = Math.max(0, parseInt(value) || 0)
    setQuantities(prev => ({
      ...prev,
      [name]: numValue
    }))
  }, [])

  const validateForm = () => {
    const newErrors = {}
    const templateData = getTemplateDataFromCanvas()

    // Validate required fields
    fieldsArray.forEach(field => {
      const isRequired = field.field_required || field.fieldRequired
      const columnName = field.name || field.column_name || field.columnName
      if (isRequired && columnName && !templateData[columnName]?.trim()) {
        newErrors[columnName] = 'This field is required'
      }
    })

    // Validate approval checkbox
    if (!approved) {
      newErrors['approval'] = 'You must approve the information before adding to cart'
    }

    // Validate at least one quantity > 0
    const totalQuantity = Object.values(quantities).reduce((sum, q) => sum + q, 0)
    if (totalQuantity === 0) {
      newErrors['quantity'] = 'Please enter at least one quantity'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      // Derive template data from the canvas fields (column_name -> text value)
      const templateData = getTemplateDataFromCanvas()
      // Get full field state for PDF generation and restoration
      const fieldState = getFullFieldState()

      // Build form data for submission
      const submitData = {
        cart_items: {
          item_id: itemId,
          template_data: templateData,
          field_state: fieldState,
          ...quantities
        }
      }

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify(submitData),
      })

      if (response.ok) {
        const data = await response.json()
        setIsDirty(false)
        setIsSubmitted(true)
        setSuccessMessage(data.message || 'Item added to cart!')
        setCheckoutUrl(data.checkout_url)

        // Update cart quantity in header if element exists
        const cartQuantityEl = document.getElementById('cart_quantity')
        if (cartQuantityEl && data.cart_items_count) {
          cartQuantityEl.textContent = data.cart_items_count
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        alert(errorData.error || 'Failed to add to cart. Please try again.')
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      alert('An error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleContinueShopping = () => {
    // Close the modal by clicking the close button
    const closeBtn = document.querySelector('.custom-modal-close-button')
    if (closeBtn) {
      closeBtn.click()
    }
  }

  // Render school selection step
  const renderSchoolSelection = () => (
    <Box className={styles.schoolSelectionContainer}>
      <Typography variant="h4" style={{ color: '#1097d2', marginBottom: '8px', fontSize: '28px' }}>
        {itemName}
      </Typography>
      {itemNumber && (
        <Typography variant="body1" color="textSecondary" style={{ marginBottom: '24px', fontSize: '16px' }}>
          Item #{itemNumber}
        </Typography>
      )}

      <Paper elevation={1} style={{ padding: '32px', maxWidth: '600px', margin: '0 auto' }}>
        <Typography variant="h4" style={{ marginBottom: '16px', textAlign: 'center', fontWeight: 500, fontSize: '26px' }}>
          Please identify your school
        </Typography>
        <Typography variant="body1" color="textSecondary" style={{ marginBottom: '28px', textAlign: 'center', fontSize: '18px' }}>
          We need this information to customize your product.
        </Typography>

        <Box mb={3}>
          <Typography variant="body1" style={{ marginBottom: '10px', fontWeight: 600, fontSize: '18px' }}>
            School District
          </Typography>
          <AsyncSelect
            cacheOptions
            defaultOptions
            placeholder="Start typing to search districts..."
            value={selectedDistrict}
            loadOptions={loadDistrictOptions}
            onChange={handleDistrictChange}
            styles={{
              control: (base) => ({ ...base, minHeight: '48px', fontSize: '16px' }),
              option: (base) => ({ ...base, fontSize: '16px', padding: '12px 16px' }),
              placeholder: (base) => ({ ...base, fontSize: '16px' }),
              singleValue: (base) => ({ ...base, fontSize: '16px' }),
              input: (base) => ({ ...base, fontSize: '16px' }),
            }}
          />
        </Box>

        {selectedDistrict && (
          <Box mb={3}>
            <Typography variant="body1" style={{ marginBottom: '10px', fontWeight: 600, fontSize: '18px' }}>
              School
            </Typography>
            <AsyncSelect
              key={selectedDistrict.value}
              cacheOptions
              defaultOptions
              placeholder="Start typing to search schools..."
              value={selectedSchool}
              loadOptions={loadSchoolOptions}
              onChange={handleSchoolChange}
              styles={{
                control: (base) => ({ ...base, minHeight: '48px', fontSize: '16px' }),
                option: (base) => ({ ...base, fontSize: '16px', padding: '12px 16px' }),
                placeholder: (base) => ({ ...base, fontSize: '16px' }),
                singleValue: (base) => ({ ...base, fontSize: '16px' }),
                input: (base) => ({ ...base, fontSize: '16px' }),
              }}
            />
          </Box>
        )}

        {selectedSchool && (
          <Box mb={3}>
            <Typography variant="body1" style={{ marginBottom: '10px', fontWeight: 600, fontSize: '18px' }}>
              Your Position
            </Typography>
            <Select
              options={positionOptions}
              value={selectedPosition}
              onChange={handlePositionChange}
              placeholder="Select your position..."
              styles={{
                control: (base) => ({ ...base, minHeight: '48px', fontSize: '16px' }),
                option: (base) => ({ ...base, fontSize: '16px', padding: '12px 16px' }),
                placeholder: (base) => ({ ...base, fontSize: '16px' }),
                singleValue: (base) => ({ ...base, fontSize: '16px' }),
              }}
            />
          </Box>
        )}

        {schoolError && (
          <Typography variant="body1" color="error" style={{ marginBottom: '16px', textAlign: 'center', fontSize: '16px' }}>
            {schoolError}
          </Typography>
        )}

        {selectedSchool && selectedPosition && (
          <Box mt={4} pt={3} style={{ borderTop: '1px solid #eee', textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSaveSchoolInfo}
              disabled={isSavingSchool}
              style={{ minWidth: '220px', fontSize: '18px', padding: '14px 32px' }}
            >
              {isSavingSchool ? 'Saving...' : 'Continue'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )

  // Handle cancel - close the modal and return to item display
  const handleCancel = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to close?')) {
      return
    }
    // Temporarily clear dirty so the X button interceptor doesn't double-prompt
    setIsDirty(false)
    const closeBtn = document.querySelector('.custom-modal-close-button')
    if (closeBtn) {
      setTimeout(() => closeBtn.click(), 0)
    }
  }

  // Handle continue to proof review - generates PDF and advances
  const handleContinueToProof = async () => {
    // Validate quantities first
    const totalQuantity = Object.values(quantities).reduce((sum, q) => sum + q, 0)
    if (totalQuantity === 0) {
      setErrors({ quantity: 'Please enter at least one quantity' })
      return
    }

    setProofError(null)

    // Generate the proof PDF
    if (generateProofUrl) {
      setIsGeneratingProof(true)

      try {
        const templateData = getTemplateDataFromCanvas()
        const fieldState = getFullFieldState()

        const response = await fetch(generateProofUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          },
          body: JSON.stringify({
            template_data: templateData,
            field_state: fieldState,
          }),
        })

        if (response.ok) {
          const data = await response.json()
          setProofPdfUrl(data.pdf_url)
          if (data.page_images && data.page_images.length > 0) {
            setProofPageImages(data.page_images)
            setProofCurrentPage(0)
          }
          setCurrentStep('proof_review')
        } else {
          const errorData = await response.json().catch(() => ({}))
          setProofError(errorData.error || 'Failed to generate proof. Please try again.')
        }
      } catch (error) {
        console.error('Error generating proof:', error)
        setProofError('An error occurred generating the proof. Please try again.')
      } finally {
        setIsGeneratingProof(false)
      }
    } else {
      // No proof URL configured, skip to proof review anyway
      setCurrentStep('proof_review')
    }
  }

  // Handle going back to edit from proof review
  const handleCorrect = () => {
    setProofPageImages([])
    setProofCurrentPage(0)
    setCurrentStep('template_edit')
  }

  // Ref for canvas panel to calculate available height
  const canvasPanelRef = useRef(null)
  const [canvasHeight, setCanvasHeight] = useState(null)

  // Calculate available height for the canvas panel
  useEffect(() => {
    if (currentStep !== 'template_edit') return

    const calculateHeight = () => {
      if (canvasPanelRef.current) {
        // Account for page tabs and padding
        const panelHeight = canvasPanelRef.current.clientHeight
        const tabsHeight = isMultipage ? 42 : 0
        setCanvasHeight(panelHeight - tabsHeight - 24) // 24px for padding
      }
    }

    // Delay to let the layout render first
    const timeout = setTimeout(calculateHeight, 150)
    window.addEventListener('resize', calculateHeight)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', calculateHeight)
    }
  }, [currentStep, isMultipage])

  // Render template editing step
  const renderTemplateEditor = () => (
    <>
      {isSubmitted ? (
        // Success state
        <Box className={styles.successContainer} textAlign="center" py={4}>
          <Typography variant="h5" style={{ color: 'green', marginBottom: '16px', fontSize: '22px' }}>
            {successMessage}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={handleContinueShopping}
              size="large"
              style={{ marginRight: '16px', fontSize: '16px', padding: '12px 24px' }}
            >
              Continue Shopping
            </Button>
            {checkoutUrl && (
              <Button
                variant="contained"
                color="primary"
                href={checkoutUrl}
                size="large"
                style={{ fontSize: '16px', padding: '12px 24px' }}
              >
                Checkout
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        // Side-by-side layout: canvas left, sidebar right
        <div className={styles.editorLayout}>
          {/* Left panel: Canvas */}
          <div className={styles.canvasPanel} ref={canvasPanelRef}>
            <div className={styles.canvasPanelInner}>
              {/* Page tabs for multipage templates */}
              {isMultipage && (
                <div className={styles.pageTabs}>
                  {allPages.map((page, idx) => (
                    <Button
                      key={page.id || 'page1'}
                      variant={currentPage === idx ? 'contained' : 'outlined'}
                      color={currentPage === idx ? 'primary' : 'default'}
                      size="small"
                      onClick={() => setCurrentPage(idx)}
                      style={{ minWidth: 80 }}
                    >
                      Page {page.position}
                    </Button>
                  ))}
                </div>
              )}

              {/* KonvaCanvas - auto-fits to both width and height */}
              <KonvaCanvas
                fields={visibleCanvasFields}
                setFields={setCanvasFields}
                imageUrl={currentImageUrl}
                template={template}
                fonts={fonts}
                hideBoundingBoxes={hideFrameEdges}
                onHideBoundingBoxesChange={setHideFrameEdges}
                showControls={false}
                showFixedToolbar={true}
                showZoomControls={true}
                showSafetyZone={showSafetyZone}
                onSafetyZoneChange={setShowSafetyZone}
                maxContainerHeight={canvasHeight}
              />
            </div>
          </div>

          {/* Right sidebar */}
          <div className={styles.sidebar}>
            {/* Item info */}
            <div className={styles.sidebarSection}>
              <h2 className={styles.sidebarTitle}>{itemName}</h2>
              <p className={styles.sidebarSubtitle}>
                {itemNumber && <>Item #{itemNumber}</>}
                {itemNumber && currentSchoolName && <> &middot; </>}
                {currentSchoolName}
              </p>
            </div>

            {/* Instructions */}
            <div className={styles.sidebarSection}>
              <div className={styles.sidebarSectionHeading}>How to Customize</div>
              <ol className={styles.instructionsList}>
                <li className={styles.instructionStep}>
                  <span className={styles.stepNumber}>1</span>
                  <span>Click on any text to select it and access the styling toolbar</span>
                </li>
                <li className={styles.instructionStep}>
                  <span className={styles.stepNumber}>2</span>
                  <span>Use the toolbar to change font, size, and color</span>
                </li>
                <li className={styles.instructionStep}>
                  <span className={styles.stepNumber}>3</span>
                  <span>Double-click text to edit the wording</span>
                </li>
                <li className={styles.instructionStep}>
                  <span className={styles.stepNumber}>4</span>
                  <span>Right-click to copy, paste, or delete elements</span>
                </li>
              </ol>
              <label className={styles.hideEdgesToggle}>
                <Checkbox
                  checked={hideFrameEdges}
                  onChange={(e) => setHideFrameEdges(e.target.checked)}
                  size="small"
                  color="primary"
                  style={{ padding: '2px 8px 2px 0' }}
                />
                Hide frame edges
              </label>
              <label className={styles.hideEdgesToggle}>
                <Checkbox
                  checked={showSafetyZone}
                  onChange={(e) => setShowSafetyZone(e.target.checked)}
                  size="small"
                  color="primary"
                  style={{ padding: '2px 8px 2px 0' }}
                />
                Show print margin
              </label>
              <button
                type="button"
                className={styles.spellCheckBtn}
                onClick={() => setShowSpellCheck(true)}
              >
                Check Spelling
              </button>
            </div>

            {/* Quantities */}
            <div className={styles.sidebarSection}>
              <div className={styles.sidebarSectionHeading}>
                Quantity <span style={{ color: 'crimson' }}>*</span>
              </div>
              <div className={styles.quantitySection}>
                {quantityOptions.map(opt => (
                  opt.inputType === 'select' ? (
                    <FormControl key={opt.name} variant="outlined" size="small" fullWidth>
                      <InputLabel style={{ fontSize: '14px' }}>{opt.label}</InputLabel>
                      <MuiSelect
                        value={quantities[opt.name]}
                        onChange={(e) => handleQuantityChange(opt.name, e.target.value)}
                        label={opt.label}
                        style={{ fontSize: '14px', background: 'white' }}
                        error={!!errors['quantity']}
                      >
                        {(opt.options || []).map(val => (
                          <MenuItem key={val} value={val} style={{ fontSize: '14px' }}>
                            {val}
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  ) : (
                    <div className={styles.quantityRow} key={opt.name}>
                      <span className={styles.quantityLabel}>{opt.label}</span>
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        value={quantities[opt.name]}
                        onChange={(e) => handleQuantityChange(opt.name, e.target.value)}
                        inputProps={{ min: 0, step: opt.step || 1, style: { fontSize: '16px', fontWeight: 600, textAlign: 'center' } }}
                        error={!!errors['quantity']}
                        className={styles.quantityInput}
                      />
                    </div>
                  )
                ))}
              </div>
              {errors['quantity'] && (
                <Typography variant="body2" color="error" style={{ fontSize: '13px', marginTop: '8px' }}>
                  {errors['quantity']}
                </Typography>
              )}
              {proofError && (
                <Typography variant="body2" color="error" style={{ fontSize: '13px', marginTop: '8px' }}>
                  {proofError}
                </Typography>
              )}
            </div>

            {/* Action buttons - pushed to bottom */}
            <div className={styles.sidebarActions}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleContinueToProof}
                disabled={isGeneratingProof}
                style={{
                  fontSize: '15px',
                  padding: '10px 16px',
                  textTransform: 'uppercase',
                  backgroundColor: '#3f51b5',
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                {isGeneratingProof ? 'Generating...' : 'Continue to Review'}
              </Button>
              <Button
                variant="outlined"
                color="default"
                fullWidth
                onClick={handleCancel}
                style={{ fontSize: '14px', padding: '8px 16px', textTransform: 'uppercase' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )

  // Render proof review step
  const renderProofReview = () => (
    <>
      {/* Header */}
      <div className={styles.header}>
        {!isSubmitted && (
          <Typography variant="h4" className={styles.itemName} style={{ color: '#1097d2', fontSize: '26px' }}>
            Review Your Proof
          </Typography>
        )}
        <Typography variant="body1" color="textSecondary" style={{ fontSize: '16px', marginTop: '8px' }}>
          {itemName} - Item #{itemNumber}
        </Typography>
        {currentSchoolName && (
          <Typography variant="body1" style={{ color: '#666', marginTop: '4px', fontSize: '16px' }}>
            School: {currentSchoolName}
          </Typography>
        )}
      </div>

      {isSubmitted ? (
        // Success state
        <Box className={styles.successContainer} textAlign="center" py={4}>
          <Typography variant="h5" style={{ color: 'green', marginBottom: '16px', fontSize: '22px' }}>
            {successMessage}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={handleContinueShopping}
              size="large"
              style={{ marginRight: '16px', fontSize: '16px', padding: '12px 24px' }}
            >
              Continue Shopping
            </Button>
            {checkoutUrl && (
              <Button
                variant="contained"
                color="primary"
                href={checkoutUrl}
                size="large"
                style={{ fontSize: '16px', padding: '12px 24px' }}
              >
                Checkout
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box>
          {/* Proof Preview */}
          <Paper elevation={1} style={{ padding: '24px', marginBottom: '24px', textAlign: 'center' }}>
            {proofPageImages.length > 0 ? (
              <>
                <Typography variant="body1" style={{ fontSize: '18px', marginBottom: '20px' }}>
                  Please review your proof carefully. Items will be printed exactly as shown.
                </Typography>
                {proofPageImages.length > 1 && (
                  <Box style={{ marginBottom: '16px' }}>
                    {proofPageImages.map((_, idx) => (
                      <Button
                        key={idx}
                        variant={proofCurrentPage === idx ? 'contained' : 'outlined'}
                        color={proofCurrentPage === idx ? 'primary' : 'default'}
                        size="small"
                        onClick={() => setProofCurrentPage(idx)}
                        style={{ minWidth: 80, marginRight: 8 }}
                      >
                        Page {idx + 1}
                      </Button>
                    ))}
                  </Box>
                )}
                <Box style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '20px',
                  background: '#f5f5f5'
                }}>
                  <img
                    src={proofPageImages[proofCurrentPage]}
                    alt={`Proof page ${proofCurrentPage + 1}`}
                    style={{ width: '100%', display: 'block' }}
                  />
                </Box>
                <Typography variant="body1" style={{ fontSize: '16px', color: 'red', fontWeight: 'bold', marginTop: '8px' }}>
                  Please check your proof carefully in order to avoid mistakes.
                </Typography>
              </>
            ) : proofPdfUrl ? (
              <>
                <Typography variant="body1" style={{ fontSize: '18px', marginBottom: '20px' }}>
                  Please review your proof carefully. Items will be printed exactly as shown.
                </Typography>
                <Box style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '20px',
                  background: '#f5f5f5'
                }}>
                  <object
                    data={`${proofPdfUrl}#view=Fit&toolbar=0&navpanes=0`}
                    type="application/pdf"
                    style={{ width: '100%', height: '80vh', border: 'none' }}
                  >
                    <Box style={{ padding: '40px', textAlign: 'center' }}>
                      <Typography variant="body1" style={{ marginBottom: '16px' }}>
                        PDF preview requires a PDF viewer. Click below to view your proof.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        href={proofPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View PDF Proof
                      </Button>
                    </Box>
                  </object>
                </Box>
                <Typography variant="body1" style={{ fontSize: '16px', color: 'red', fontWeight: 'bold', marginTop: '8px' }}>
                  Please check your proof carefully in order to avoid mistakes.
                </Typography>
              </>
            ) : (
              <Typography variant="body1" style={{ fontSize: '16px', color: '#666' }}>
                Proof preview not available. Please verify your design before adding to cart.
              </Typography>
            )}
          </Paper>

          {/* Approval checkbox */}
          <Paper elevation={1} style={{ padding: '20px', marginBottom: '24px' }}>
            <FormControlLabel
              style={{ marginLeft: 0, gap: '4px', alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={approved}
                  onChange={(e) => {
                    setApproved(e.target.checked)
                    if (e.target.checked && errors['approval']) {
                      setErrors(prev => {
                        const newErrors = { ...prev }
                        delete newErrors['approval']
                        return newErrors
                      })
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" style={{ fontSize: '16px' }}>
                  By checking this box, I approve all info is correct and free of errors.
                  Items will be printed with the exact wording provided.
                  <span style={{ color: 'crimson', fontStyle: 'italic', marginLeft: '4px' }}>
                    * Required
                  </span>
                </Typography>
              }
            />
            {errors['approval'] && (
              <Typography variant="body2" color="error" display="block" style={{ marginTop: '8px', fontSize: '14px' }}>
                {errors['approval']}
              </Typography>
            )}
          </Paper>

          {/* Correct and Add to Cart buttons */}
          <Box display="flex" justifyContent="center" gap={3} mt={3}>
            <Button
              variant="outlined"
              color="default"
              size="large"
              onClick={handleCorrect}
              style={{ minWidth: '160px', fontSize: '16px', padding: '12px 28px' }}
            >
              Correct
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ minWidth: '180px', fontSize: '16px', padding: '12px 28px' }}
            >
              {isSubmitting ? 'Adding to Cart...' : 'Add to Cart'}
            </Button>
          </Box>
        </Box>
      )}
    </>
  )

  // Handle spell check field update
  const handleSpellCheckFieldUpdate = useCallback((fieldId, text) => {
    setCanvasFields(prev => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], text, sampleText: null }
    }))
  }, [])

  return (
    <div className={styles.container}>
      {currentStep === 'school_info' && renderSchoolSelection()}
      {currentStep === 'template_edit' && renderTemplateEditor()}
      {currentStep === 'proof_review' && renderProofReview()}

      {/* Spell check panel */}
      {showSpellCheck && (
        <SpellCheckPanel
          fields={canvasFields}
          onFieldUpdate={handleSpellCheckFieldUpdate}
          onClose={() => setShowSpellCheck(false)}
        />
      )}
    </div>
  )
}
